import React from 'react'
import { plusIcon } from 'assets/svg/svgs'
import { MenuSvgs } from 'assets'
import { t } from 'i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from 'components'
import {
    constants,
    findPermission,
    getConnectUser,
    isFieldAgent
} from 'helpers'
import { useBaseNavigation } from 'hooks/useBaseNavigation'

export type FcNavBarConfigParams = {
    permissionsState?: string[]
    hasUnreadNotifications?: boolean
    clearClientSearchHandler?: () => void
    clearSelectedFilter?: ({
        selectedFilter
    }: {
        selectedFilter: string | undefined
    }) => void
}

/**
 * Village client list only: /village/<id>.
 */
const OPENED_VILLAGE_PATH = /^\/village\/([^/]+)$/

export const getOpenedVillageIdFromPath = (
    pathname: string
): string | undefined => {
    const normalizedPath = pathname.replace(/\/$/, '')
    const match = OPENED_VILLAGE_PATH.exec(normalizedPath)
    if (!match) {
        return undefined
    }
    const villageId = match[1]
    if (villageId === 'select-village') {
        return undefined
    }
    return villageId
}

export const useNavBarConfig = ({
    permissionsState = [],
    clearClientSearchHandler = () => undefined
}: FcNavBarConfigParams = {}) => {
    const navigate = useBaseNavigation()
    const routerNavigate = useNavigate()
    // Path gates the add-client item; do not cache this config across navigations.
    const { pathname } = useLocation()
    const { HomeIcon, VillagesIcon, ProfileIcon, NotificationMessageIcon } =
        MenuSvgs

    const openedVillageId = getOpenedVillageIdFromPath(pathname)
    const canEnroll = findPermission(permissionsState, constants.UPDATE_CLIENT)
    const showAddClient =
        isFieldAgent(getConnectUser().role) && canEnroll && !!openedVillageId

    const addClientNavHandler = () => {
        if (!openedVillageId) {
            return
        }
        clearClientSearchHandler()
        routerNavigate(
            `/client-search?villageId=${encodeURIComponent(openedVillageId)}`
        )
    }

    const addClientMenuItem = {
        name: (
            <span className="menu-plus" aria-label={t('menu.addClient')}>
                {plusIcon({})}
            </span>
        ),
        path: 'client-search',
        href: 'client-search',
        disableActive: true,
        onClickCallback: addClientNavHandler,
        isVisible: true
    }

    const addClientDesktopMenuItem = {
        name: (
            <Button
                icon={plusIcon({})}
                iconPos="left"
                size="small"
                style="primary"
            >
                {t('menu.addClient').toString()}
            </Button>
        ),
        path: 'client-search',
        href: 'client-search',
        disableActive: true,
        onClickCallback: addClientNavHandler,
        isVisible: true
    }

    const mobileMenuConfig = [
        {
            name: t('menu.home'),
            path: 'home',
            href: 'home',
            icon: <HomeIcon />,
            isVisible: true,
            onClickCallback: () => navigate('/home')
        },
        {
            name: t('menu.villages'),
            path: 'village',
            href: 'village',
            icon: <VillagesIcon />,
            isVisible: true,
            onClickCallback: () => navigate('/village')
        },
        ...(showAddClient ? [addClientMenuItem] : []),
        {
            name: t('messages'),
            path: 'notifications',
            href: 'notification/messages',
            icon: <NotificationMessageIcon />,
            isVisible: true,
            onClickCallback: () => navigate('/notification/messages')
        },
        {
            name: t('menu.profile'),
            path: 'profile',
            href: 'profile',
            icon: <ProfileIcon />,
            isVisible: true,
            onClickCallback: () => navigate('/profile')
        }
    ]

    const desktopMenuConfig = [
        {
            name: t('menu.home'),
            path: 'home',
            href: 'home',
            icon: <HomeIcon />,
            isVisible: true,
            onClickCallback: () => navigate('/home')
        },
        {
            name: t('menu.village'),
            path: 'village',
            href: 'village',
            icon: <VillagesIcon />,
            isVisible: true,
            onClickCallback: () => navigate('/village')
        },
        ...(showAddClient ? [addClientDesktopMenuItem] : []),
        {
            name: t('messages'),
            path: 'notifications',
            href: 'notification/messages',
            icon: <NotificationMessageIcon />,
            isVisible: true,
            onClickCallback: () => navigate('/notification/messages')
        },
        {
            name: t('menu.profile'),
            path: 'profile',
            href: 'profile',
            icon: <ProfileIcon />,
            isVisible: true,
            onClickCallback: () => navigate('/profile')
        }
    ]

    return { mobileMenuConfig, desktopMenuConfig }
}
